import React, { useEffect, useMemo, useRef, useState } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Divider from '@mui/material/Divider';
import Icon from '@mui/material/Icon';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme as useMuiTheme, alpha } from '@mui/material/styles';

import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';

import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import MDButton from 'components/MDButton';
import MDInput from 'components/MDInput';

import { minToHm } from '../utils/time';
import { TrainerSection } from '../sections/TrainerSection';
import { CustomerSection } from '../sections/CustomerSection';
import { sessionsApi } from 'sectors/pilates/sessions/api/sessionsApi';

// ---- utils
function clean(x) {
  const s = (x ?? '').toString().trim().replace(/\s+/g, ' ');
  return s.length ? s : '';
}
function digitsOnly(x) {
  return (x ?? '').toString().replace(/[^\d+]/g, '');
}
function pickMsg(err) {
  return err?.data?.message || err?.response?.data?.message || err?.message || 'İşlem başarısız.';
}

// decision helpers (backend shape: {allowed, blocks[], warnings[]})
const normList = (arr) =>
  (Array.isArray(arr) ? arr : []).map((x) => ({
    code: x?.code || null,
    title: x?.title || null,
    message: x?.message || (typeof x === 'string' ? x : JSON.stringify(x)),
  }));

/**
 * ✅ ConfirmDialog (DecisionDialog tasarımıyla aynı)
 * - items varsa liste gösterir, yoksa message gösterir
 * - hideConfirm = true => sadece "Kapat" (block senaryosu gibi)
 */
function ConfirmDialog({
  open,
  onClose,
  onConfirm,

  title = 'Onay',
  subtitle,
  message,

  items, // [{title,message}] veya string[]
  tone = 'warning', // "warning" | "error" | "success" | "info"

  confirmText = 'Onayla',
  cancelText = 'Kapat',

  busy = false,
  hideConfirm = false,
  tokens,
}) {
  const theme = useMuiTheme();

  const toneColor = useMemo(() => {
    const t = theme.palette?.[tone]?.main;
    return t || theme.palette.warning.main;
  }, [theme, tone]);

  const icon = useMemo(() => {
    if (tone === 'error') return 'block';
    if (tone === 'success') return 'check_circle';
    if (tone === 'info') return 'info';
    return 'warning';
  }, [tone]);

  const list = useMemo(() => {
    if (!items) return [];
    const arr = Array.isArray(items) ? items : [];
    return arr.map((x) => {
      if (typeof x === 'string') return { title: null, message: x };
      return { title: x?.title ?? null, message: x?.message ?? '' };
    });
  }, [items]);

  return (
    <Dialog open={!!open} onClose={busy ? undefined : onClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ pb: 1.2 }}>
        <MDBox sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <MDBox
            sx={{
              width: 40,
              height: 40,
              borderRadius: '14px',
              display: 'grid',
              placeItems: 'center',
              background: alpha(toneColor, 0.12),
              border: `1px solid ${alpha(toneColor, 0.22)}`,
              flex: '0 0 auto',
            }}
          >
            <Icon sx={{ color: toneColor, fontSize: 20 }}>{icon}</Icon>
          </MDBox>

          <MDBox sx={{ minWidth: 0 }}>
            <MDTypography variant="h6" fontWeight="bold" sx={{ lineHeight: 1.1 }}>
              {title}
            </MDTypography>
            {subtitle ? (
              <MDTypography variant="caption" sx={{ opacity: 0.8, display: 'block', mt: 0.35 }}>
                {subtitle}
              </MDTypography>
            ) : null}
          </MDBox>
        </MDBox>
      </DialogTitle>

      <Divider />

      <DialogContent sx={{ pt: 2 }}>
        {list.length ? (
          <MDBox sx={{ display: 'grid', gap: 1 }}>
            {list.map((x, i) => (
              <MDBox
                key={i}
                sx={{
                  p: 1.2,
                  borderRadius: '16px',
                  border: `1px solid ${alpha(toneColor, 0.25)}`,
                  background: alpha(toneColor, tokens?.isDark ? 0.08 : 0.06),
                }}
              >
                <MDTypography variant="button" fontWeight="bold" sx={{ display: 'block' }}>
                  {x.title || (tone === 'error' ? 'Engel' : 'Uyarı')}
                </MDTypography>
                <MDTypography variant="caption" sx={{ opacity: 0.82, display: 'block', mt: 0.4 }}>
                  {x.message}
                </MDTypography>
              </MDBox>
            ))}
          </MDBox>
        ) : (
          <MDTypography
            variant="button"
            sx={{ opacity: 0.9, whiteSpace: 'pre-line', lineHeight: 1.5 }}
          >
            {message || 'Emin misin?'}
          </MDTypography>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 2.2, py: 1.6, gap: 1, display: 'flex', justifyContent: 'flex-end' }}>
        <MDButton
          variant="outlined"
          color="dark"
          onClick={onClose}
          disabled={busy}
          sx={{ borderRadius: '14px', textTransform: 'none', fontWeight: 800 }}
        >
          {cancelText}
        </MDButton>

        {!hideConfirm ? (
          <MDButton
            variant="contained"
            color={
              tone === 'error'
                ? 'error'
                : tone === 'success'
                ? 'success'
                : tone === 'info'
                ? 'info'
                : 'warning'
            }
            onClick={onConfirm}
            disabled={busy}
            sx={{ borderRadius: '14px', textTransform: 'none', fontWeight: 900 }}
          >
            {busy ? 'Kaydediliyor...' : confirmText}
          </MDButton>
        ) : null}
      </DialogActions>
    </Dialog>
  );
}

/**
 * ✅ DecisionConfirmDialog:
 * backend decision -> ConfirmDialog props
 * - blocks varsa: error + proceed yok
 * - warnings varsa: warning + "Yine de Kaydet"
 */
function DecisionConfirmDialog({ open, onClose, onProceed, decision, busy = false, tokens }) {
  const blocks = normList(decision?.blocks);
  const warnings = normList(decision?.warnings);
  const isBlock = blocks.length > 0;
  const items = isBlock ? blocks : warnings;

  return (
    <ConfirmDialog
      open={open}
      onClose={onClose}
      onConfirm={onProceed}
      tone={isBlock ? 'error' : 'warning'}
      title={isBlock ? 'İşlem Engellendi' : 'Uyarılar'}
      subtitle={
        isBlock
          ? 'Bu seans kaydedilemez. Aşağıdaki engelleri düzelt.'
          : 'Devam edersen seans kaydedilecek. İstersen yine de kaydedebilirsin.'
      }
      items={items}
      confirmText="Yine de Kaydet"
      cancelText="Kapat"
      hideConfirm={isBlock} // ✅ blocks => proceed yok
      busy={busy}
      tokens={tokens}
    />
  );
}

// ✅ Ücretsiz seans tipi seçenekleri
const FREE_SESSION_TYPES = [{ value: 'APPOINTMENT', label: 'Randevu' }];

export function SessionCreateFreeModal({
  open,
  onClose,
  onBack,
  onCreated,

  dayISO,
  startMin,
  defaultTrainerId = 'ALL',
  tokens,
  trainers = [],
  staffLoading = false,
  staffError = false,
}) {
  const theme = useMuiTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));

  // 0 = MEMBER, 1 = GUEST
  const [mode, setMode] = useState(0);

  // trainer
  const [trainerId, setTrainerId] = useState('');

  // member selection
  const [memberIds, setMemberIds] = useState([]);

  // guest fields
  const [guestName, setGuestName] = useState('');
  const [guestPhone, setGuestPhone] = useState('');

  // free config
  const [durationMin, setDurationMin] = useState(60);
  const [sessionType, setSessionType] = useState('APPOINTMENT');
  const [note, setNote] = useState('');

  // decision
  const [decisionOpen, setDecisionOpen] = useState(false);
  const [decision, setDecision] = useState(null);

  // busy
  const [saving, setSaving] = useState(false);
  const lastPayloadRef = useRef(null);

  // reset on open
  useEffect(() => {
    if (!open) return;

    setMode(0);
    setTrainerId('');
    setMemberIds([]);

    setGuestName('');
    setGuestPhone('');

    setDurationMin(60);
    setSessionType('APPOINTMENT');
    setNote('');

    setDecision(null);
    setDecisionOpen(false);

    setSaving(false);
    lastPayloadRef.current = null;
  }, [open]);

  // default trainer
  useEffect(() => {
    if (!open) return;
    const list = Array.isArray(trainers) ? trainers : [];
    const tId =
      defaultTrainerId !== 'ALL'
        ? String(defaultTrainerId)
        : list[0]?.id != null
        ? String(list[0].id)
        : '';
    setTrainerId((prev) => prev || tId);
  }, [open, defaultTrainerId, trainers]);

  const title = dayISO ? `${dayISO} • ${minToHm(startMin)}` : 'Ücretsiz Seans';

  const selectedTrainerName = useMemo(() => {
    const t = (trainers || []).find((x) => String(x.id) === String(trainerId));
    return clean(t?.name) || null;
  }, [trainerId, trainers]);

  const canSave = useMemo(() => {
    if (!open) return false;
    if (!dayISO || typeof startMin !== 'number') return false;
    if (!trainerId) return false;

    if (mode === 0) return (memberIds?.length || 0) > 0;

    if (!clean(guestName)) return false;
    // telefon opsiyonel yapacaksan aşağıdaki kontrolü kaldır:
    if (!digitsOnly(guestPhone)) return false;

    return true;
  }, [open, dayISO, startMin, trainerId, mode, memberIds, guestName, guestPhone]);

  // ✅ FREE DTO için minimal participant shape
  const buildParticipants = () => {
    if (mode === 0) {
      return (memberIds || []).map((mid) => ({
        memberId: Number(mid),
        fullName: null,
        phone: null,
      }));
    }
    return [
      {
        memberId: null,
        fullName: clean(guestName),
        phone: digitsOnly(guestPhone) || null,
      },
    ];
  };

  const buildPayload = () => ({
    dayISO,
    startMin: Number(startMin),
    durationMin: Number(durationMin) || 60,

    trainerId: Number(trainerId),
    trainerName: selectedTrainerName || null,

    activityId: null,
    activityName: 'Ücretsiz Seans',

    capacity: Math.max(1, mode === 0 ? memberIds.length : 1),

    sessionType,
    note: clean(note) || null,

    participants: buildParticipants(),
  });

  const doValidate = async (payload) => {
    const res = await sessionsApi.validateFreeSession(payload);
    return res?.data ?? res;
  };

  const doCreate = async (payload) => {
    const res = await sessionsApi.createFreeSession(payload);
    return res?.data ?? res;
  };

  const handleSave = async () => {
    if (!canSave || saving) return;

    const payload = buildPayload();
    lastPayloadRef.current = payload;

    setSaving(true);
    try {
      const dec = await doValidate(payload);
      setDecision(dec);

      const blocks = Array.isArray(dec?.blocks) ? dec.blocks : [];
      const warnings = Array.isArray(dec?.warnings) ? dec.warnings : [];

      if (blocks.length || warnings.length) {
        setDecisionOpen(true);
        return;
      }

      await doCreate(payload);
      onClose?.();
      await onCreated?.();
    } catch (err) {
      console.error('[SessionCreateFreeModal] save FAILED', err);
      alert(pickMsg(err) || 'Ücretsiz seans kaydedilemedi.');
    } finally {
      setSaving(false);
    }
  };

  const handleProceedDespiteWarnings = async () => {
    if (saving) return;
    const payload = lastPayloadRef.current || buildPayload();

    setSaving(true);
    try {
      await doCreate(payload);
      setDecisionOpen(false);
      onClose?.();
      await onCreated?.();
    } catch (err) {
      console.error('[SessionCreateFreeModal] create despite warnings FAILED', err);
      alert(pickMsg(err) || 'Ücretsiz seans kaydedilemedi.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={saving ? undefined : onClose}
        fullScreen={fullScreen}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: {
            borderRadius: fullScreen ? 0 : '22px',
            overflow: 'hidden',
            background: tokens?.isDark ? alpha('#0b1220', 0.92) : alpha('#ffffff', 0.98),
            border: `1px solid ${tokens?.border || theme.palette.divider}`,
            boxShadow: fullScreen
              ? 'none'
              : '0 22px 60px rgba(0,0,0,.16), 0 10px 22px rgba(0,0,0,.12)',
            backgroundImage: 'none',
          },
        }}
        sx={{
          '& .MuiBackdrop-root': {
            backgroundColor: fullScreen ? 'rgba(0,0,0,.55)' : 'rgba(0,0,0,.45)',
            backdropFilter: 'blur(6px)',
          },
        }}
      >
        <DialogTitle sx={{ pb: 1.1 }}>
          <MDBox display="flex" alignItems="center" justifyContent="space-between" gap={1}>
            <MDBox display="flex" alignItems="center" gap={1.2} sx={{ minWidth: 0 }}>
              <MDBox
                sx={{
                  width: 42,
                  height: 42,
                  borderRadius: '16px',
                  display: 'grid',
                  placeItems: 'center',
                  background: alpha(theme.palette.success.main, 0.12),
                  border: `1px solid ${alpha(theme.palette.success.main, 0.22)}`,
                  flex: '0 0 auto',
                }}
              >
                <Icon sx={{ fontSize: 20, color: theme.palette.success.main }}>
                  volunteer_activism
                </Icon>
              </MDBox>

              <MDBox sx={{ minWidth: 0 }}>
                <MDTypography variant="h6" fontWeight="bold" sx={{ lineHeight: 1.1 }}>
                  Ücretsiz Seans
                </MDTypography>
                <MDTypography variant="caption" sx={{ opacity: 0.78, display: 'block', mt: 0.35 }}>
                  {title}
                </MDTypography>
              </MDBox>
            </MDBox>

            <MDBox display="flex" gap={0.6}>
              <MDButton
                variant="text"
                color="secondary"
                onClick={saving ? undefined : onBack}
                disabled={saving}
                sx={{ minWidth: 44, width: 44, height: 44, borderRadius: '14px' }}
              >
                <Icon>arrow_back</Icon>
              </MDButton>
              <MDButton
                variant="text"
                color="secondary"
                onClick={saving ? undefined : onClose}
                disabled={saving}
                sx={{ minWidth: 44, width: 44, height: 44, borderRadius: '14px' }}
              >
                <Icon>close</Icon>
              </MDButton>
            </MDBox>
          </MDBox>
        </DialogTitle>

        <Divider />

        <DialogContent sx={{ pt: 1.6, pb: 1.3 }}>
          <MDBox sx={{ display: 'grid', gap: 1.1 }}>
            <TrainerSection
              sx={tokens?.card}
              tokens={tokens}
              trainerId={trainerId}
              setTrainerId={setTrainerId}
              trainers={trainers}
              loading={staffLoading}
              error={staffError}
            />

            {/* Member vs Guest */}
            <MDBox sx={{ ...tokens?.card, p: 1.2, borderRadius: '18px' }}>
              <Tabs
                value={mode}
                onChange={(_, v) => setMode(v)}
                variant="fullWidth"
                sx={{
                  mb: 1,
                  minHeight: 38,
                  '& .MuiTab-root': { minHeight: 38, fontWeight: 900, textTransform: 'none' },
                }}
              >
                <Tab label="Üye" />
                <Tab label="Misafir" />
              </Tabs>

              {mode === 0 ? (
                <CustomerSection
                  tokens={tokens}
                  trainerId={trainerId}
                  selectedIds={memberIds}
                  setSelectedIds={setMemberIds}
                />
              ) : (
                <MDBox sx={{ display: 'grid', gap: 1 }}>
                  <MDInput
                    label="Ad Soyad"
                    value={guestName}
                    onChange={(e) => setGuestName(e.target.value)}
                    fullWidth
                  />
                  <MDInput
                    label="Telefon"
                    value={guestPhone}
                    onChange={(e) => setGuestPhone(e.target.value)}
                    fullWidth
                  />
                </MDBox>
              )}
            </MDBox>

            {/* Free config */}
            <MDBox sx={{ ...tokens?.card, p: 1.2, borderRadius: '18px' }}>
              <MDBox sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
                <MDInput
                  type="number"
                  label="Süre (dk)"
                  value={durationMin}
                  onChange={(e) => setDurationMin(Number(e.target.value || 60))}
                  fullWidth
                />

                <FormControl fullWidth>
                  <InputLabel id="free-session-type-label">Seans Tipi</InputLabel>
                  <Select
                    labelId="free-session-type-label"
                    label="Seans Tipi"
                    value={sessionType}
                    onChange={(e) => setSessionType(e.target.value)}
                    sx={{ borderRadius: '14px' }}
                  >
                    {FREE_SESSION_TYPES.map((opt) => (
                      <MenuItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </MDBox>

              <MDBox sx={{ mt: 1 }}>
                <MDInput
                  label="Not (opsiyonel)"
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  fullWidth
                  multiline
                  rows={2}
                />
              </MDBox>
            </MDBox>
          </MDBox>
        </DialogContent>

        <Divider />

        <DialogActions
          sx={{
            p: 2,
            pt: 1.4,
            gap: 1,
            display: 'flex',
            justifyContent: 'space-between',
            flexDirection: fullScreen ? 'column' : 'row',
          }}
        >
          <MDButton
            variant="outlined"
            color="dark"
            onClick={onClose}
            disabled={saving}
            fullWidth={fullScreen}
            sx={{ borderRadius: '16px', textTransform: 'none', fontWeight: 900, height: 44 }}
          >
            Vazgeç
          </MDButton>

          <MDButton
            variant="gradient"
            color="success"
            onClick={handleSave}
            disabled={!canSave || saving}
            fullWidth={fullScreen}
            sx={{ borderRadius: '16px', textTransform: 'none', fontWeight: 900, height: 44 }}
          >
            {saving ? 'Kontrol ediliyor...' : 'Kaydet'}
          </MDButton>
        </DialogActions>
      </Dialog>

      {/* ✅ validate warnings/blocks dialog (ARTIK ConfirmDialog tabanlı) */}
      <DecisionConfirmDialog
        open={decisionOpen}
        onClose={() => setDecisionOpen(false)}
        onProceed={handleProceedDespiteWarnings}
        decision={decision}
        busy={saving}
        tokens={tokens}
      />
    </>
  );
}
